import React from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash-es';
import dateFormat from 'dateformat';
import { stripHtml } from 'client/utils/string-utils';
import { InventoryEntities } from 'client/data/models/inventory';
import { numberWithCommas } from 'site-modules/shared/utils/string';
import { isCpo } from 'site-modules/shared/utils/inventory-utils/is-cpo';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { transformInventoryComments } from 'site-modules/shared/utils/inventory/seller-comment-utils';
import { SrpCardLeaseInfo } from 'site-modules/shared/components/inventory/srp-card/srp-card-lease-info';
import { SrpCardVehicleHistory } from './srp-card-vehicle-history';

import './srp-card-more-info.scss';

export function SrpCardMoreInfo({ vinData = {}, visitorCity = '', visitorStateCode = '', displayLeaseInfo = false }) {
  const sellersComments = transformInventoryComments(get(vinData, 'sellersComments', ''));
  const dealerCity = get(vinData, 'dealerInfo.address.city', '');
  const stateCode = get(vinData, 'dealerInfo.address.stateCode', '');
  const distance = get(vinData, 'dealerInfo.distance', 0);

  const historyInfo = get(vinData, 'historyInfo', {});
  const styleInfo = get(vinData, 'vehicleInfo.styleInfo', {});
  const vin = get(vinData, 'vin', '');
  const stockNumber = get(vinData, 'stockNumber', '');
  const type = get(vinData, 'type', '');
  const fuel = get(styleInfo, 'fuel', {});
  const epaCombinedMPG = get(fuel, 'epaCombinedMPG', 'N/A');
  const epaCityMPG = get(fuel, 'epaCityMPG', 'N/A');
  const epaHighwayMPG = get(fuel, 'epaHighwayMPG', 'N/A');

  const listedSince = get(vinData, 'listedSince');

  const hasCustomerAddress = distance && visitorCity && visitorStateCode;
  const isUsedType = isUsed(type);
  const hasFuelInfo = !isEmpty(fuel);
  const hasListingInformation = !!(vin || stockNumber || isUsedType || listedSince);

  return (
    <>
      {displayLeaseInfo && <SrpCardLeaseInfo vinData={vinData} />}
      <p className="small fw-bold">
        Located in {dealerCity}, {stateCode}{' '}
        {!!hasCustomerAddress && `/ ${numberWithCommas(distance)} miles away from ${visitorCity}, ${visitorStateCode}`}
      </p>
      {!!sellersComments && <p className="xsmall mb-1">{stripHtml(sellersComments)}</p>}

      <dl>
        {isUsedType && <SrpCardVehicleHistory historyInfo={historyInfo} />}
        {hasFuelInfo && (
          <>
            <dt className="small fw-bold">Features and Specs:</dt>
            <dd className="xsmall mb-1">
              {epaCombinedMPG} Combined MPG ({epaCityMPG} City/{epaHighwayMPG} Highway)
            </dd>
          </>
        )}
        {hasListingInformation && (
          <>
            <dt className="small fw-bold">Listing Information:</dt>
            <dd className="xsmall mb-1">
              <dl className="mb-0">
                {vin && (
                  <div>
                    <dt className="d-inline fw-normal me-0_25">VIN:</dt>
                    <dd className="d-inline mb-0">{vin}</dd>
                  </div>
                )}
                {stockNumber && (
                  <div>
                    <dt className="d-inline fw-normal me-0_25">Stock:</dt>
                    <dd className="d-inline mb-0">{stockNumber}</dd>
                  </div>
                )}
                {isUsedType && (
                  <div>
                    <dt className="d-inline fw-normal me-0_25">Certified Pre-Owned:</dt>
                    <dd className="d-inline mb-0">{isCpo(type) ? 'Yes' : 'No'}</dd>
                  </div>
                )}
                {listedSince && (
                  <div>
                    <dt className="d-inline fw-normal me-0_25">Listed since:</dt>
                    <dd className="d-inline mb-0">{dateFormat(listedSince, 'mm-dd-yyyy')}</dd>
                  </div>
                )}
              </dl>
            </dd>
          </>
        )}
      </dl>
    </>
  );
}

SrpCardMoreInfo.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  visitorCity: PropTypes.string,
  visitorStateCode: PropTypes.string,
  displayLeaseInfo: PropTypes.bool,
};
